// Dot rows for each grid level + open orders + unmatched openings + current price.
// Sorted desc by price. Mirrors legacy renderGridDots exactly.
import type { GridConfig, Order, Trade } from "@/types/api";
import { fmt } from "@/lib/format";

interface GridLevelsViewProps {
  grid: GridConfig;
  orders: Order[];
  unmatchedOpenings: Trade[];
  livePrice: number | null;
}

function createGridLevels(lower: number, upper: number, count: number): number[] {
  const step = (upper - lower) / count;
  const levels: number[] = [];
  for (let i = 0; i <= count; i++) levels.push(+(lower + step * i).toFixed(8));
  return levels;
}

type RowKind = "level" | "exit" | "held" | "current";

interface Row {
  price: number;
  kind: RowKind;
  level?: number;
  order?: Order;
  held?: Trade;
  exitOrder?: Order;
  mergedCurrent?: boolean;
  isRelaxed?: boolean;
}

const DOT_CLASS: Record<string, string> = {
  "buy-open": "w-3 h-3 rounded-full border-2 border-success bg-success/30",
  "sell-open": "w-3 h-3 rounded-full border-2 border-danger bg-danger/30",
  "buy-filled": "w-3 h-3 rounded-full bg-success",
  "sell-filled": "w-3 h-3 rounded-full bg-danger",
  "held-buy": "w-3 h-3 rounded-full bg-success border-2 border-success",
  "held-sell": "w-3 h-3 rounded-full bg-danger border-2 border-danger",
  cancelled: "w-3 h-3 rounded-full bg-muted/30 border border-muted",
  pending: "w-3 h-3 rounded-full bg-border border border-muted",
  "exit-buy": "w-3 h-3 rounded-full border-2",
  "exit-sell": "w-3 h-3 rounded-full border-2",
  current: "w-3 h-3 rounded-full bg-warn",
};

export default function GridLevelsView({
  grid,
  orders,
  unmatchedOpenings,
  livePrice,
}: GridLevelsViewProps) {
  const levels = createGridLevels(grid.lower_price, grid.upper_price, grid.grid_count);
  const currentPrice = livePrice ?? 0;

  // Separate regular level orders from exit-limit orders
  const orderMap: Record<number, Order> = {};
  const exitOrders: Order[] = [];
  for (const o of orders) {
    if (o.status !== "open" && o.status !== "pending") continue;
    const lvl = levels[o.grid_level];
    const isExit =
      o.opening_trade_id != null && lvl != null && Math.abs(o.price - lvl) / lvl > 0.0005;
    if (isExit) exitOrders.push(o);
    else orderMap[o.grid_level] = o;
  }

  // Build rows
  const rows: Row[] = levels.map((p, i) => ({
    price: p,
    kind: "level",
    level: i,
    order: orderMap[i],
  }));

  for (const eo of exitOrders) {
    const opener = unmatchedOpenings.find((u) => u.id === eo.opening_trade_id);
    const maxHoldMs = (grid.max_hold_minutes ?? 0) * 60_000;
    const isRelaxed =
      opener != null &&
      maxHoldMs > 0 &&
      Date.now() - new Date(opener.traded_at).getTime() >= maxHoldMs * 2;
    rows.push({ price: eo.price, kind: "exit", exitOrder: eo, isRelaxed });
  }

  for (const u of unmatchedOpenings) {
    rows.push({ price: u.price, kind: "held", held: u });
  }

  if (currentPrice) rows.push({ price: currentPrice, kind: "current" });

  rows.sort((a, b) => b.price - a.price);

  // Current price always shown as its own row — no merging with grid levels.

  return (
    <div className="mb-4 space-y-0.5 max-h-80 overflow-y-auto pr-1">
      {rows.map((r, i) => {
        if (r.kind === "current") {
          return (
            <div key={`cur-${i}`} className="flex items-center gap-2 py-0.5">
              <div className={DOT_CLASS.current} />
              <span className="text-warn text-xs font-mono">{fmt(r.price, grid.symbol)}</span>
              <span className="text-warn text-[0.65rem]">current price</span>
            </div>
          );
        }

        if (r.kind === "held" && r.held) {
          const u = r.held;
          const color = u.side === "buy" ? "text-success" : "text-danger";
          const dotCls = `held-${u.side}`;
          const pctDiff = currentPrice
            ? ((u.price - currentPrice) / currentPrice) * 100
            : null;
          return (
            <div key={`held-${u.id}`} className="flex items-center gap-2 py-0.5">
              <div className={DOT_CLASS[dotCls] ?? DOT_CLASS.pending} />
              <span className={`text-xs font-mono ${color}`}>{fmt(u.price, grid.symbol)}</span>
              <span className={`text-[0.65rem] ${color}`}>
                {pctDiff != null
                  ? `(${pctDiff >= 0 ? "+" : ""}${pctDiff.toFixed(2)}%) `
                  : ""}
                {u.side.toUpperCase()} held @ {fmt(u.price, grid.symbol)}
              </span>
            </div>
          );
        }

        if (r.kind === "exit" && r.exitOrder) {
          const o = r.exitOrder;
          const exitColor = r.isRelaxed ? "text-info" : "text-accent";
          const exitBorderColor = r.isRelaxed ? "#3b82f6" : "#a855f7";
          const pctDiff = currentPrice
            ? ((o.price - currentPrice) / currentPrice) * 100
            : null;
          return (
            <div
              key={`exit-${o.id}`}
              className="flex items-center gap-2 py-0.5 rounded"
              style={{ background: `${exitBorderColor}10` }}
            >
              <div
                className={DOT_CLASS[`exit-${o.side}`]}
                style={{ borderColor: exitBorderColor, background: `${exitBorderColor}20` }}
              />
              <span className={`text-xs font-mono ${exitColor}`}>{fmt(o.price, grid.symbol)}</span>
              <span className={`text-[0.65rem] ${exitColor}`}>
                {pctDiff != null
                  ? `(${pctDiff >= 0 ? "+" : ""}${pctDiff.toFixed(2)}%) `
                  : ""}
                {o.side.toUpperCase()} BE close
              </span>
            </div>
          );
        }

        // Level row
        const order = r.order;
        let dotKey = "pending";
        let label = "";
        if (order) {
          const s = order.side;
          const st = order.status;
          if (st === "open") { dotKey = `${s}-open`; label = `${s.toUpperCase()} open`; }
          else if (st === "filled") { dotKey = `${s}-filled`; label = `${s.toUpperCase()} filled`; }
          else if (st === "cancelled") { dotKey = "cancelled"; label = "cancelled"; }
          else { dotKey = "pending"; label = "pending"; }
        }
        const pctDiff = currentPrice
          ? ((r.price - currentPrice) / currentPrice) * 100
          : null;

        if (r.mergedCurrent) {
          return (
            <div key={`lvl-${r.level}`} className="flex items-center gap-2 py-0.5 bg-warn/5 rounded">
              <div className={DOT_CLASS[dotKey] ?? DOT_CLASS.pending} />
              <span className="text-xs font-mono text-warn">{fmt(r.price, grid.symbol)}</span>
              <span className="text-warn text-[0.65rem]">
                {label ? `${label} (L${r.level}) · ` : ""}current price
              </span>
            </div>
          );
        }

        return (
          <div key={`lvl-${r.level}`} className="flex items-center gap-2 py-0.5">
            <div className={DOT_CLASS[dotKey] ?? DOT_CLASS.pending} />
            <span className="text-xs font-mono text-dim">{fmt(r.price, grid.symbol)}</span>
            <span className="text-muted text-[0.65rem]">
              {pctDiff != null ? `(${pctDiff >= 0 ? "+" : ""}${pctDiff.toFixed(2)}%) ` : ""}
              {label} {r.level != null ? `(L${r.level})` : ""}
            </span>
          </div>
        );
      })}
    </div>
  );
}
